import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const NOTE_W = 268;
const GAP = 14;
const TOP_PAD = 8;

function resolveZIndex(anchorEl) {
  const overlay = anchorEl?.closest?.('.open-os-v2-mock-overlay');
  if (overlay) {
    const z = parseInt(window.getComputedStyle(overlay).zIndex, 10);
    if (Number.isFinite(z)) return z + 80;
  }
  return 20100;
}

/**
 * Stick note no vão ao lado do modal de histórico (topo da viewport).
 */
export default function OpenOsV2ProdStickNote({
  open,
  medias = [],
  comparativo = null,
  anchorRef = null,
}) {
  const [dismissed, setDismissed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [pos, setPos] = useState(null);

  const wantOpen = !!open && !dismissed;

  useEffect(() => {
    if (!open) setDismissed(false);
  }, [open]);

  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    let timer = 0;
    if (wantOpen) {
      setMounted(true);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setShown(true));
      });
    } else {
      setShown(false);
      timer = window.setTimeout(() => {
        setMounted(false);
        setPos(null);
      }, 340);
    }
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (timer) window.clearTimeout(timer);
    };
  }, [wantOpen]);

  useLayoutEffect(() => {
    if (!mounted) return undefined;

    const place = () => {
      const el = anchorRef?.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceRight = window.innerWidth - r.right - 8;
      let left;
      if (spaceRight >= NOTE_W + GAP) {
        const gapCenter = r.right + Math.max(GAP, (spaceRight - NOTE_W) / 2);
        left = Math.min(window.innerWidth - NOTE_W - 10, gapCenter);
      } else {
        left = Math.max(8, r.left - NOTE_W - GAP);
      }
      const top = TOP_PAD;
      const zIndex = resolveZIndex(el);
      setPos((prev) =>
        prev && prev.top === top && prev.left === left && prev.zIndex === zIndex
          ? prev
          : { top, left, zIndex },
      );
    };

    place();
    const t = window.setTimeout(place, 40);
    window.addEventListener('resize', place);
    const ro =
      typeof ResizeObserver !== 'undefined' && anchorRef?.current
        ? new ResizeObserver(place)
        : null;
    if (anchorRef?.current) ro?.observe(anchorRef.current);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', place);
      ro?.disconnect();
    };
  }, [mounted, anchorRef, shown, wantOpen]);

  if (!mounted || !pos || typeof document === 'undefined') return null;

  return createPortal(
    <aside
      className={'oosv2-prod-sticknote oosv2-prod-sticknote--float' + (shown ? ' is-open' : '')}
      style={{ top: pos.top, left: pos.left, zIndex: pos.zIndex }}
      aria-live="polite"
      aria-label="Resumo de produção"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="oosv2-prod-sticknote-pin" aria-hidden="true" />
      <div className="oosv2-prod-sticknote-headbar">
        <div className="oosv2-prod-sticknote-title">Produção</div>
        <button
          type="button"
          className="oosv2-prod-sticknote-close"
          aria-label="Fechar resumo de produção"
          onClick={(e) => {
            e.stopPropagation();
            setDismissed(true);
          }}
        >
          ×
        </button>
      </div>
      {(medias || []).length > 0 ? (
        <div className="oosv2-prod-sticknote-medias">
          <div className="oosv2-prod-sticknote-head" aria-hidden="true">
            <span />
            <span />
            <span>Média</span>
            <span>Último</span>
          </div>
          {(medias || []).map((m) => (
            <div key={m.key} className="oosv2-prod-sticknote-row">
              <span className="oosv2-prod-sticknote-dot" style={{ background: m.cor }} />
              <span className="oosv2-prod-sticknote-label">{m.label}</span>
              <span className="oosv2-prod-sticknote-val" title="Média mensal">
                {m.mediaLabel}
              </span>
              <span
                className="oosv2-prod-sticknote-val oosv2-prod-sticknote-val--ultimo"
                title="Último contador (MEDATUAL)"
              >
                {m.ultimoLabel || '—'}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {comparativo ? (
        <div className="oosv2-prod-sticknote-foot">
          <div className="oosv2-prod-sticknote-meta">
            <span>
              Total <strong>{comparativo.totalLabel}</strong>
            </span>
            <span>
              Média <strong>{comparativo.mediaMensalLabel}</strong>
            </span>
            {comparativo.cicloMensalLabel ? (
              <span>
                Ciclo <strong>{comparativo.cicloMensalLabel}</strong>
              </span>
            ) : null}
          </div>
          {comparativo.pctLabel ? (
            <div
              className={
                'oosv2-prod-sticknote-pct' +
                (comparativo.acimaMedia ? ' is-up' : ' is-down')
              }
            >
              <strong>{comparativo.pctLabel}</strong> {comparativo.texto}
            </div>
          ) : null}
        </div>
      ) : null}
    </aside>,
    document.body,
  );
}
